/**
 * How far an ability actually reaches.
 *
 * A bot that presses buttons on a timer looks like a bot. A bot that presses
 * them when the shape would CONNECT looks like a player, and it costs one
 * number per hit kind — read off the same `AbilityDef` the sim resolves
 * against, so the two can never disagree about what "in range" means.
 */
import { GROUND_ZONE_OFFSET, PROJECTILE_LAUNCH_COS, PROJECTILE_LAUNCH_SIN } from "@chaos/sim";
/**
 * Flat-ground travel of a lobbed shell: the ballistic range of a projectile
 * launched at the sim's fixed elevation. `Telegraphs.js` draws its landing ring
 * at this distance, so a Talon that fires at this range is firing where the
 * decal says the shell lands.
 */
export function projectileRange(speed, gravity, maxRange) {
    const vy = speed * PROJECTILE_LAUNCH_SIN;
    const vxz = speed * PROJECTILE_LAUNCH_COS;
    const flight = (2 * vy) / gravity;
    return Math.min(maxRange, vxz * flight);
}
/**
 * The distance at which this ability is worth pressing.
 *
 * For shapes that emanate from the body this is simply their reach. For shapes
 * placed downrange (Barrage lands 14m ahead, Arc Shot at its ballistic ring) it
 * is where the damage actually appears — a Talon that walks into melee before
 * firing Barrage drops it 14m BEHIND the enemy.
 */
export function abilityReach(ability) {
    const hit = ability.hit;
    switch (hit.kind) {
        case "melee_cone":
            return hit.range;
        case "sphere":
            return hit.radius;
        case "dash_line":
            // Blink Strike travels `selfMove` and strikes along the way; Fault Line
            // has no selfMove and simply reaches `length`.
            return ability.selfMove && ability.selfMove > 0 ? ability.selfMove : hit.length;
        case "ground_zone":
            return GROUND_ZONE_OFFSET[ability.id] ?? hit.radius;
        case "projectile":
            return projectileRange(hit.speed, hit.gravity, hit.maxRange);
        case "self":
            return 0;
        default:
            return 0;
    }
}
/**
 * How much slop to allow around `abilityReach`.
 *
 * Placed shapes (Barrage, Arc Shot) land in a circle, so anywhere inside that
 * circle counts and the tolerance is its radius. Body shapes reach outward from
 * zero, so anything closer than the reach is fine and only the far edge matters.
 */
export function reachTolerance(ability) {
    const hit = ability.hit;
    if (hit.kind === "ground_zone")
        return hit.radius;
    if (hit.kind === "projectile")
        return hit.radius;
    return 0;
}
/** True when a target at `dist` is inside what this ability would actually hit. */
export function inReach(ability, dist) {
    const reach = abilityReach(ability);
    const tol = reachTolerance(ability);
    if (tol > 0)
        return Math.abs(dist - reach) <= tol;
    return dist <= reach;
}
//# sourceMappingURL=ranges.js.map